// VITE_API_URL is set from wrangler.toml env vars:
// - Local: http://localhost:8787/api (from env.local)
// - Preview: https://dev.promo.kareotest.com/api (from env.preview)
// - Prod: https://promo.kareotest.com/api (from env.production)
import { APIError } from '@promo/shared';
import { authService } from '../services/auth';
import { navigationService } from '../services/navigation';

const API_BASE_URL = import.meta.env.VITE_API_URL;

if (!API_BASE_URL) {
  throw new Error('VITE_API_URL environment variable is required');
}

// Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
// Queue for requests that are waiting for the refresh to complete
let refreshPromise: Promise<void> | null = null;

// Helper to construct the API URL
function constructApiUrl(endpoint: string): URL {
  // Always construct absolute URL with the Worker URL
  return new URL(`${API_BASE_URL}${endpoint}`);
}

// Helper for handling Date objects in JSON serialization
const dateReviver = (_key: string, value: unknown): unknown => {
  // Check if value might be an ISO date string
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value)) {
    return new Date(value);
  }
  return value;
};

// Custom JSON.stringify to handle Date objects
function serializeBody(body: unknown): string {
  return JSON.stringify(body, (_key, value) => {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  });
}

function extractErrorMessage(data: unknown): string {
  if (typeof data !== 'object' || data === null) {
    return 'An error occurred';
  }

  const response = data as {
    message?: unknown;
    error?: unknown;
  };

  if (typeof response.error === 'object' && response.error !== null) {
    const nestedError = response.error as { message?: unknown };
    if (typeof nestedError.message === 'string') {
      return nestedError.message;
    }

    const nestedValidationMessage = extractValidationDetails(response.error);
    if (nestedValidationMessage) {
      return nestedValidationMessage;
    }
  }

  if (typeof response.message === 'string') {
    return response.message;
  }

  if (typeof response.error === 'string') {
    return response.error;
  }

  const validationMessage = extractValidationDetails(data);
  if (validationMessage) {
    return validationMessage;
  }

  return 'An error occurred';
}

function extractValidationDetails(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null) return undefined;

  const objectValue = value as {
    details?: unknown;
    issues?: unknown;
    errors?: unknown;
    fieldErrors?: unknown;
  };

  return (
    formatIssueList(objectValue.issues) ??
    formatIssueList(objectValue.errors) ??
    formatFieldErrors(objectValue.fieldErrors) ??
    extractValidationDetails(objectValue.details)
  );
}

function formatIssueList(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined;

  const messages = value
    .map((issue) => {
      if (typeof issue === 'string') return issue;
      if (typeof issue !== 'object' || issue === null) return undefined;

      const issueObject = issue as { path?: unknown; message?: unknown };
      if (typeof issueObject.message !== 'string') return undefined;

      const field = Array.isArray(issueObject.path)
        ? issueObject.path
            .filter((part) => typeof part === 'string' || typeof part === 'number')
            .join('.')
        : '';

      return field ? `${field}: ${issueObject.message}` : issueObject.message;
    })
    .filter((message): message is string => Boolean(message));

  return messages.length > 0 ? messages.join('; ') : undefined;
}

function formatFieldErrors(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null) return undefined;

  const messages = Object.entries(value as Record<string, unknown>).flatMap(
    ([field, fieldMessages]) => {
      if (!Array.isArray(fieldMessages)) return [];
      return fieldMessages
        .filter((message): message is string => typeof message === 'string')
        .map((message) => `${field}: ${message}`);
    }
  );

  return messages.length > 0 ? messages.join('; ') : undefined;
}

/**
 * Attempts to refresh the session when a 401 error occurs
 * @returns Promise that resolves when refresh is complete or rejects if refresh fails
 */
async function attemptRefresh(): Promise<void> {
  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Check if auth service is ready and user has a refresh token
  if (!authService.isReady() || !authService.hasRefreshToken()) {
    throw new Error('Cannot refresh: no auth service or refresh token');
  }

  isRefreshing = true;
  refreshPromise = authService.refreshSession().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

/**
 * Handles 401 errors by attempting token refresh or redirecting to login
 * @param endpoint - The endpoint that failed (for logging/debugging)
 * @param error - The APIError that occurred
 */
async function handle401Error(endpoint: string, error: APIError): Promise<never> {
  // Skip auth-related endpoints to prevent infinite loops
  const isAuthEndpoint = endpoint.startsWith('/auth/');

  if (isAuthEndpoint) {
    // For auth endpoints, just throw the error - no retry logic
    throw error;
  }

  try {
    // Attempt to refresh the session
    await attemptRefresh();

    // If refresh succeeded, throw a special error that callers can catch
    // to retry their original request
    throw new RefreshSuccessError('Token refreshed, retry request');
  } catch (refreshError) {
    // If refresh failed or no refresh token available, redirect to login
    console.warn(`Authentication failed for ${endpoint}, redirecting to login`, refreshError);

    // Preserve current location for post-login redirect
    if (navigationService.isReady()) {
      navigationService.redirectToLogin();
    } else {
      console.warn('Navigation service not ready, cannot redirect to login');
    }

    // Still throw the original 401 error
    throw error;
  }
}

/**
 * Special error class to indicate that a token refresh was successful
 * and the original request should be retried
 */
class RefreshSuccessError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RefreshSuccessError';
  }
}

async function handleResponse(response: Response, endpoint: string = 'unknown') {
  // Handle DELETE 204 No Content special case
  if (response.status === 204) {
    return undefined; // Return undefined for successful deletes with no content
  }

  const text = await response.text();

  try {
    // Use the reviver to convert date strings to Date objects
    const data = text ? JSON.parse(text, dateReviver) : {};

    if (!response.ok) {
      const apiError = new APIError(extractErrorMessage(data), response.status, data);

      // Handle 401 errors with automatic refresh and redirect logic
      if (response.status === 401) {
        await handle401Error(endpoint, apiError);
      }

      throw apiError;
    }

    return data;
  } catch (error) {
    if (error instanceof APIError || error instanceof RefreshSuccessError) {
      throw error;
    }

    // If JSON parsing fails, throw appropriate error
    if (error instanceof SyntaxError) {
      throw new Error(`Error parsing API response: ${error.message}`);
    }

    throw error;
  }
}

/**
 * Helper function to make requests with automatic retry on successful token refresh
 */
async function makeRequestWithRetry<T>(
  requestFn: () => Promise<Response>,
  endpoint: string
): Promise<T> {
  try {
    const response = await requestFn();
    return await handleResponse(response, endpoint);
  } catch (error) {
    // If refresh was successful, retry the original request once
    if (error instanceof RefreshSuccessError) {
      console.log(`Retrying request to ${endpoint} after successful token refresh`);
      const retryResponse = await requestFn();
      return await handleResponse(retryResponse, endpoint);
    }

    throw error;
  }
}

export async function get<T = unknown>(
  endpoint: string,
  params?: Record<string, string>
): Promise<T> {
  const url = constructApiUrl(endpoint);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }

  return makeRequestWithRetry<T>(
    () =>
      fetch(url.toString(), {
        method: 'GET',
        credentials: 'include', // For handling cookies
      }),
    endpoint
  );
}

export async function post<T = unknown>(endpoint: string, body: unknown): Promise<T> {
  const url = constructApiUrl(endpoint);

  return makeRequestWithRetry<T>(
    () =>
      fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: serializeBody(body),
        credentials: 'include', // For handling cookies
      }),
    endpoint
  );
}

export async function put<T = unknown>(endpoint: string, body: unknown): Promise<T> {
  const url = constructApiUrl(endpoint);

  return makeRequestWithRetry<T>(
    () =>
      fetch(url.toString(), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: serializeBody(body),
        credentials: 'include',
      }),
    endpoint
  );
}

export async function patch<T = unknown>(endpoint: string, body: unknown): Promise<T> {
  const url = constructApiUrl(endpoint);

  return makeRequestWithRetry<T>(
    () =>
      fetch(url.toString(), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: serializeBody(body),
        credentials: 'include',
      }),
    endpoint
  );
}

// Using deleteFn name to avoid conflict with reserved keyword
export async function deleteFn<T = unknown>(endpoint: string): Promise<T> {
  const url = constructApiUrl(endpoint);

  return makeRequestWithRetry<T>(
    () =>
      fetch(url.toString(), {
        method: 'DELETE',
        credentials: 'include',
      }),
    endpoint
  );
}
